import InfoIcon from '@mui/icons-material/Info';
import { Box, Typography, useTheme } from '@mui/material';

import RemoveButton from '@/components/shared/RemoveButton';

interface TableSelectionActionsProps {
  /** Number of selected items */
  selectedCount: number;
  /** Total number of items in the current view */
  totalCount: number;
  /** Callback when remove button is clicked */
  onRemove: () => void;
  /** Custom label for the selection info (default: "Selected Users") */
  selectionLabel?: string;
  /** Custom text for the remove button (default: "Remove Users") */
  removeButtonText?: string;
  /** Test ID prefix for data-testid attributes */
  testIdPrefix?: string;
  /** Whether the remove action is disabled (overrides selectedCount === 0) */
  disabled?: boolean;
}

function TableSelectionActions({
  selectedCount,
  totalCount,
  onRemove,
  selectionLabel = 'Selected Users',
  removeButtonText = 'Remove Users',
  testIdPrefix = 'table',
  disabled,
}: TableSelectionActionsProps) {
  const theme = useTheme();
  const isRemoveDisabled = disabled ?? selectedCount === 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.75,
          borderRadius: '3px',
          backgroundColor: theme.hhglobal.neutral.gray100,
          border: '0',
        }}
      >
        <InfoIcon
          sx={{
            fontSize: 18,
            color: theme.hhglobal.utility.info[800],
          }}
        />
        <Typography
          sx={{
            fontSize: theme.hhglobal.typography.body.base.fontSize,
            color: theme.hhglobal.neutral.superblack,
            whiteSpace: 'nowrap',
          }}
          data-testid={`${testIdPrefix}-selection-info`}
        >
          {selectionLabel}: <strong>{selectedCount}</strong> out of <strong>{totalCount}</strong>
        </Typography>
      </Box>
      <RemoveButton variant='text' data-testid={`${testIdPrefix}-remove-button`} disabled={isRemoveDisabled} onClick={onRemove}>
        {removeButtonText}
      </RemoveButton>
    </Box>
  );
}

export default TableSelectionActions;
